import type { SourceMappingConfig } from "@/features/admin/lib/price-mappings-types";

export const adminKeys = {
  // `null` is the signed-out slot, so an anonymous "no access" answer can
  // never shadow a user's real one after sign-in.
  me: (userId: string | null) => ["admin", "me", userId] as const,
  sets: ["admin", "sets"] as const,
  sources: ["admin", "sources"] as const,
  reviewQueue: ["admin", "review-queue"] as const,
  workspace: ["admin", "workspace"] as const,
  workspaceQueue: ["admin", "workspace", "queue"] as const,
  workspaceSameDifference: (params: {
    provider: string;
    field: string;
    value: string;
    currentValue?: string;
  }) => ["admin", "workspace", "same-difference", params] as const,
  workspaceRecords: Object.assign(
    (params: { kind: string; q?: string; limit?: number; cursor?: string }) =>
      ["admin", "workspace", "records", params] as const,
    { prefix: ["admin", "workspace", "records"] as const },
  ),
  cards: {
    all: ["admin", "cards"] as const,
    list: ["admin", "cards", "list"] as const,
    errataAnnouncements: ["admin", "cards", "errata-announcements"] as const,
    detail: (cardId: string) => ["admin", "cards", "detail", cardId] as const,
    unmatched: (name: string) => ["admin", "cards", "unmatched", name] as const,
    allCards: ["admin", "cards", "all-cards"] as const,
  },
  // Not nested under `cards`: invalidating a card's detail on every
  // citation write would refetch the whole candidate review payload.
  printingCitations: (printingId: string) =>
    ["admin", "printings", printingId, "citations"] as const,
  printingDesk: {
    all: ["admin", "printing-desk"] as const,
    list: (mode: string) => ["admin", "printing-desk", "list", mode] as const,
    cardPrintings: (cardSlug: string) => ["admin", "printing-desk", "card", cardSlug] as const,
    printing: Object.assign(
      (printingId: string) => ["admin", "printing-desk", "printing", printingId] as const,
      { prefix: ["admin", "printing-desk", "printing"] as const },
    ),
  },
  audit: {
    events: (filters: { actorUserId?: string; action?: string; search?: string }) =>
      ["admin", "audit-events", filters] as const,
    actors: ["admin", "audit-actors"] as const,
    actions: ["admin", "audit-actions"] as const,
  },
  printingEvents: ["admin", "printing-events"] as const,
  marketplace: (marketplace: string) => ["admin", marketplace] as const,
  marketplaceGroups: ["admin", "marketplace-groups"] as const,
  groupBanners: ["admin", "group-banners"] as const,
  boardStates: ["admin", "board-states"] as const,
  featureFlags: ["admin", "feature-flags"] as const,
  featureFlagOverrides: ["admin", "feature-flag-overrides"] as const,
  grants: ["admin", "grants"] as const,
  siteSettings: ["admin", "site-settings"] as const,
  status: ["admin", "status"] as const,
  dashboard: ["admin", "dashboard"] as const,
  jobRuns: ["admin", "job-runs"] as const,
  jobRunsList: (params: { page: number; kind?: string; trigger?: string; status?: string }) =>
    ["admin", "job-runs", "list", params] as const,
  jobRunsByKind: (kind: string) => ["admin", "job-runs", "by-kind", kind] as const,
  jobSchedules: ["admin", "job-schedules"] as const,
  cacheStatus: ["admin", "cache-status"] as const,
  siblingVariantDrift: ["admin", "sibling-variant-drift"] as const,
  apiKeys: ["admin", "api-keys"] as const,
  rehostStatus: ["admin", "rehost-status"] as const,
  brokenImages: ["admin", "broken-images"] as const,
  lowResImages: ["admin", "low-res-images"] as const,
  missingImages: ["admin", "missing-images"] as const,
  priceMappings: {
    bySource: (config: SourceMappingConfig) => ["admin", config.source] as const,
    bySourceAndFilter: (config: SourceMappingConfig, showAll: boolean) =>
      ["admin", config.source, "mappings", { all: showAll }] as const,
  },
  unifiedMappings: {
    all: ["admin", "unified-mappings"] as const,
    list: ["admin", "unified-mappings", "list"] as const,
    summary: ["admin", "unified-mappings", "summary"] as const,
    byCard: (cardId: string) => ["admin", "unified-mappings", "card", cardId] as const,
  },
  ignoredProducts: ["admin", "ignored-products"] as const,
  ignoredCandidates: ["admin", "ignored-candidates"] as const,
  deckZones: ["admin", "deck-zones"] as const,
  domains: ["admin", "domains"] as const,
  languages: ["admin", "languages"] as const,
  finishes: ["admin", "finishes"] as const,
  artVariants: ["admin", "art-variants"] as const,
  rarities: ["admin", "rarities"] as const,
  cardTypes: ["admin", "card-types"] as const,
  superTypes: ["admin", "super-types"] as const,
  deckFormats: ["admin", "deck-formats"] as const,
  formats: ["admin", "formats"] as const,
  markers: ["admin", "markers"] as const,
  meta: {
    // Prefix every event read sits under, so a write that moves an event's
    // counts refetches whichever page is on screen, plus any open detail.
    events: ["admin", "meta", "events"] as const,
    eventList: (params: {
      page: number;
      search?: string;
      format?: string;
      dateFrom?: string;
      dateTo?: string;
      incompleteStandings?: boolean;
      noDecks?: boolean;
      sort?: string;
      direction?: string;
    }) => ["admin", "meta", "events", "list", params] as const,
    eventSearch: (search: string) => ["admin", "meta", "events", "search", search] as const,
    event: (eventId: string) => ["admin", "meta", "events", eventId] as const,
    eventPlayers: (eventId: string) => ["admin", "meta", "events", eventId, "players"] as const,
    eventSources: (eventId: string) => ["admin", "meta", "events", eventId, "sources"] as const,
    eventSourceStates: (eventId: string) =>
      ["admin", "meta", "events", eventId, "source-states"] as const,
    eventUploads: (eventId: string) => ["admin", "meta", "events", eventId, "uploads"] as const,
    crossSource: (eventId: string) => ["admin", "meta", "events", eventId, "cross-source"] as const,
    // Suggestion keys nest under it on purpose, so settling an overlay also
    // refetches the ranked targets that settling invalidates.
    overlays: ["admin", "meta", "overlays"] as const,
    eventSuggestions: (overlayId: string) =>
      ["admin", "meta", "overlays", overlayId, "event-suggestions"] as const,
    playerSuggestions: (overlayId: string) =>
      ["admin", "meta", "overlays", overlayId, "player-suggestions"] as const,
    // Null for a provider's overlay, which is an answer and gets cached as one.
    submissionForPlayerOverlay: (overlayId: string) =>
      ["admin", "meta", "overlays", overlayId, "submission"] as const,
    eventCorrections: ["admin", "meta", "event-corrections"] as const,
    ignoredSources: ["admin", "meta", "ignored-sources"] as const,
    // Prefix every source's filtered pages sit under, so a write to one row
    // refetches whichever page is on screen.
    mirror: ["admin", "meta", "mirror"] as const,
    mirrorList: (params: object) => ["admin", "meta", "mirror", "list", params] as const,
    syncSettings: ["admin", "meta", "sync", "settings"] as const,
    archiveJobs: ["admin", "meta", "archive", "jobs"] as const,
    syncStatus: Object.assign(
      (source: string) => ["admin", "meta", "sync", "status", source] as const,
      { prefix: ["admin", "meta", "sync", "status"] as const },
    ),
    sourceTemplates: ["admin", "meta", "source", "templates"] as const,
    sourceFormats: ["admin", "meta", "source", "formats"] as const,
  },
  customTags: ["admin", "custom-tags"] as const,
  customTagCategories: ["admin", "custom-tag-categories"] as const,
  cardTags: ["admin", "card-tags"] as const,
  tagCategories: ["admin", "tag-categories"] as const,
  cardCustomTags: Object.assign(
    (cardId: string) => ["admin", "card-custom-tags", cardId] as const,
    { prefix: ["admin", "card-custom-tags"] as const },
  ),
  distributionChannels: ["admin", "distribution-channels"] as const,
  distinctArtists: ["admin", "distinct-artists"] as const,
  providerSettings: ["admin", "provider-settings"] as const,
  cardBans: Object.assign((cardId: string) => ["admin", "card-bans", cardId] as const, {
    prefix: ["admin", "card-bans"] as const,
  }),
  cardErrata: Object.assign((cardId: string) => ["admin", "card-errata", cardId] as const, {
    prefix: ["admin", "card-errata"] as const,
  }),
  keywordStats: ["admin", "keyword-stats"] as const,
  typographyReview: ["admin", "typography-review"] as const,
  rules: {
    versions: ["admin", "rules", "versions"] as const,
  },
  users: ["admin", "users"] as const,
} as const;
